import { useMemo } from 'react';

import qs from 'qs';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useQueryParamValue } from '#src/app/shared/hooks/location';

import { useEncodedBanditDataForUrl } from '../useEncodedBanditDataForUrl';

type Result = ModelHookResult<{
	shareButtonUrl: string;
	promoteButtonUrl: string;
	advocateEntryPointUrl: string;
}>;

export function usePromoteOrShareActionUrls(): Result {
	const {
		data: {
			petition: { slug },
		},
	} = usePsfPromoteOrSharePageData();
	const commonParams = useCommonParams();

	return {
		data: {
			shareButtonUrl: useMemo(
				() =>
					`/p/${encodeURIComponent(slug)}/psf/share${qs.stringify(
						{
							...commonParams,
							share_intent: 1,
						},
						{ addQueryPrefix: true },
					)}`,
				[slug, commonParams],
			),
			promoteButtonUrl: useMemo(
				() => `/p/${encodeURIComponent(slug)}/promote${qs.stringify(commonParams, { addQueryPrefix: true })}`,
				[slug, commonParams],
			),
			advocateEntryPointUrl: useMemo(() => `/p/${encodeURIComponent(slug)}/advocates`, [slug]),
		},
	};
}

function useCommonParams() {
	const allowActions = useQueryParamValue('allow_actions');
	const sourceLocation = useQueryParamValue('source_location');
	const guest = useQueryParamValue('guest');
	const shortDisplayName = useQueryParamValue('short_display_name');
	const {
		data: { query: banditQueryParams },
	} = useEncodedBanditDataForUrl();

	const {
		data: {
			chipInAmount,
			bandits: {
				psfCombo: { comboExpVariant },
			},
		},
	} = usePsfPromoteOrSharePageData();

	return useMemo(
		() => ({
			source_location: sourceLocation || 'combo_psf',
			psf_variant: 'combo',
			...(['new', 'existing'].includes(guest || '') ? { from_guest_psf: '1' } : undefined),
			...(shortDisplayName ? { short_display_name: shortDisplayName } : undefined),
			...(allowActions ? { allow_actions: allowActions } : undefined),
			...banditQueryParams,
			...(comboExpVariant?.includes('static')
				? {
						psf_combo_static_amount: chipInAmount.toString(),
					}
				: undefined),
		}),
		[sourceLocation, guest, shortDisplayName, allowActions, banditQueryParams, comboExpVariant, chipInAmount],
	);
}
